/**
 * Futuristic Execution Modal - Command Center Style
 * Modal for initiating new pipeline executions with futuristic design
 */

import React, { useState, useEffect } from 'react';
import { X, Play, Zap, ChevronRight, Cpu } from 'lucide-react';
import { useDashboardServerStore } from '@/stores/dashboard-server';
import { PipelineConfig } from '@/types/execution';
import EnhancedCurriculumBrowser from '@/components/curriculum/EnhancedCurriculumBrowser';
import '@/styles/futuristic.css';
import { useProfiles } from '@/hooks/useProfiles';

interface FuturisticExecutionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VISUAL_STYLES = {
  raw_authentic: {
    label: 'RAW AUTHENTIC',
    description: 'Industrial textures, harsh lighting',
    color: 'cyan',
    glow: 'rgba(6, 182, 212'
  },
  digital_rebellion: {
    label: 'DIGITAL REBELLION',
    description: 'Glitch aesthetics, neon darkness',
    color: 'purple',
    glow: 'rgba(168, 85, 247'
  },
  visceral_truth: {
    label: 'VISCERAL TRUTH',
    description: 'High contrast, emergency lighting',
    color: 'error',
    glow: 'rgba(239, 68, 68'
  }
} as const;

const FuturisticExecutionModal: React.FC<FuturisticExecutionModalProps> = ({
  isOpen,
  onClose
}) => {
  const startNewExecution = useDashboardServerStore((s) => s.startNewExecution);
  const { profiles } = useProfiles('owner');
  
  const [config, setConfig] = useState({
    selected_lesson_path: '',
    selected_curriculum_path: '', // Original path for UI selection
    lesson_content: null as any,
    scene_description: '',
    visual_style: 'raw_authentic',
    video_duration: 30,
    budget_limit: 50,
    quality_threshold: 80,
    max_iterations: 3,
    enable_checkpointing: true,
    custom_instructions: '',
    profile_id: '' as string | null
  });

  const [isInitiating, setIsInitiating] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Preselect default profile (if any) when profiles list loads
  useEffect(() => {
    if (!config.profile_id && profiles && profiles.length > 0) {
      const preferred = profiles.find((p) => (p as any).is_default) || profiles[0];
      if (preferred?.id) {
        setConfig((prev) => ({ ...prev, profile_id: preferred.id }));
      }
    }
  }, [profiles]);

  // Handle curriculum file selection
  const handleFileSelect = (filePath: string, fileContent: any) => {
    // Transform curriculum path to media path format
    // From: /content/curriculum/baseline-en-US/drafts/content/<module>/day<num>.json
    // To: content/curriculum/baseline-en-US/media/<module>/<day>_lesson
    
    let mediaPath = filePath;
    
    // Extract module and day from the path
    const pathMatch = filePath.match(/\/content\/curriculum\/(baseline-[^\/]+)\/drafts\/content\/([^\/]+)\/(day\d+)\.json$/);
    if (pathMatch) {
      const [, baselineLang, module, day] = pathMatch;
      // Create media path in the expected format
      mediaPath = `content/curriculum/${baselineLang}/media/${module}/${day}_lesson`;
    }
    
    setConfig(prev => ({
      ...prev,
      selected_lesson_path: mediaPath,
      selected_curriculum_path: filePath, // Store original path for UI selection
      lesson_content: fileContent
    }));
  };

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setIsInitiating(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    setIsInitiating(true);
    
    // Create the pipeline configuration with user inputs
    // Note: scene_description, visual_style, and video_duration are stored in stage configs
    const pipelineConfig: PipelineConfig = {
      scene_id: `scene-${Date.now()}`,
      asset_path: config.selected_lesson_path || '/assets',
      
      script: createStageConfig(90, 85, config.max_iterations),
      vision: createStageConfig(90, 85, config.max_iterations),
      storyboard: createStageConfig(85, 80, config.max_iterations),
      shots: createStageConfig(85, 80, config.max_iterations),
      continuity: createStageConfig(85, 80, config.max_iterations),
      frames: createFrameConfig(),
      videos: createVideoConfig(),
      
      budget_limit_usd: config.budget_limit,
      quality_target: config.quality_threshold >= 90 ? 'production' : 
                     config.quality_threshold >= 70 ? 'preview' : 'draft',
      parallel_execution: true,
      auto_retry: true,
      checkpoint_frequency: config.enable_checkpointing ? 'every_stage' : 'manual',
      
      // Optional context - profile selection
      profile_id: config.profile_id || undefined
    };
    
    // Start the execution immediately
    startNewExecution(pipelineConfig);
    
    // Close modal after a brief delay
    setTimeout(() => {
      setIsInitiating(false);
      onClose();
      // Reset form for next use
      setConfig({
        selected_lesson_path: '',
        selected_curriculum_path: '',
        lesson_content: null,
        scene_description: '',
        visual_style: 'raw_authentic',
        video_duration: 30,
        budget_limit: 50,
        quality_threshold: 80,
        max_iterations: 3,
        enable_checkpointing: true,
        custom_instructions: '',
        profile_id: ''
      });
      setCurrentStep(1);
    }, 1500);
  };

  const createStageConfig = (target: number, fallback: number, maxIter: number) => ({
    enabled: true,
    target_threshold: target,
    fallback_threshold: fallback,
    max_iterations: maxIter,
    strategy_switch_after: 2,
    primary_model: 'gpt-5',
    max_cost_per_shot: 10,
    timeout_seconds: 300,
    custom_instructions: config.custom_instructions
  });

  const createFrameConfig = () => ({
    ...createStageConfig(85, 80, config.max_iterations),
    use_reference_images: true,
    enforce_image_style: true,
    adaptive_strategy: true,
    load_continuity_context: true,
    cross_shot_continuity: true,
    aspect_ratio: '16:9' as const,
    quality_preset: 'photorealistic' as const
  });

  const createVideoConfig = () => ({
    ...createStageConfig(85, 80, config.max_iterations),
    primary_provider: 'kling' as const,
    backup_provider: 'runway' as const,
    motion_intensity: 'medium' as const,
    keyframe_mode: 'start_end' as const,
    resolution: '1080p' as const,
    frame_rate: '30' as const,
    complete_creative_context: true,
    strategic_prompting: true,
    adaptive_motion_strategy: true
  });

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 animate-fadeIn"
        onClick={(e) => {
          // Only close if clicking the backdrop directly
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden animate-zoomIn">
          {/* Background effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-purple-500/10 rounded-2xl" />
          
          {/* Main container */}
          <div className="relative bg-black/90 backdrop-blur-xl border border-cyan-500/20 rounded-2xl shadow-2xl">
            {/* Header */}
            <div className="relative p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-light tracking-wide text-white/90">
                        PIPELINE INITIALIZATION
                      </h2>
                      {config.selected_lesson_path && (
                        <span className="text-sm text-cyan-400/70 font-mono">
                          [{config.selected_lesson_path.split('/').slice(-2).join('/')}]
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/50 font-mono mt-1">
                      CONFIGURE EXECUTION PARAMETERS
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all"
                >
                  <X className="w-4 h-4 text-white/50" />
                </button>
              </div>
              
              {/* Progress bar */}
              <div className="absolute bottom-0 left-0 right-0 h-px bg-white/5">
                <div 
                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500"
                  style={{ width: `${(currentStep / 4) * 100}%` }}
                />
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
              {/* Profile selector */}
              <div className="mb-6">
                <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">
                  <span className="inline-flex items-center gap-2">
                    Creative Profile
                    <span
                      className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 font-mono"
                      title="Your chosen Creative Profile determines personas, rubrics, and models per stage. Choose wisely."
                    >
                      hint
                    </span>
                  </span>
                </label>
                <select
                  value={config.profile_id || ''}
                  onChange={(e) => setConfig({ ...config, profile_id: e.target.value || null })}
                  className="w-full bg-black/50 border border-cyan-500/30 rounded-lg p-3 text-white/90 font-mono text-sm focus:outline-none focus:border-cyan-500/50 focus:bg-black/70 transition-all"
                >
                  <option value="">&lt;None&gt; (use defaults)</option>
                  {profiles.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Step 1: Curriculum Selection */}
              {currentStep === 1 && (
                <div className="space-y-6 animate-fadeIn">
                  <div>
                    <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">
                      Select Curriculum Lesson
                    </label>
                    <div className="h-[400px] border border-cyan-500/30 rounded-lg overflow-hidden bg-black/30">
                      <EnhancedCurriculumBrowser
                        selectedFile={config.selected_curriculum_path}
                        onFileSelect={handleFileSelect}
                      />
                    </div>
                    {config.selected_lesson_path && (
                      <p className="text-xs text-cyan-400/70 mt-2 font-mono">
                        ✓ SELECTED: {config.selected_lesson_path.split('/').slice(-2).join('/')}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => setCurrentStep(2)}
                    disabled={!config.selected_lesson_path}
                    className={`w-full py-3 rounded-lg font-mono transition-all
                             flex items-center justify-center gap-2 ${
                      config.selected_lesson_path
                        ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-400 hover:from-cyan-500/30 hover:to-blue-500/30'
                        : 'bg-gray-500/20 border border-gray-500/30 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    CONTINUE
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Step 2: Video Description & Style */}
              {currentStep === 2 && (
                <div className="space-y-6 animate-fadeIn">
                  <div>
                    <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">
                      Video Description
                    </label>
                    <textarea
                      value={config.scene_description}
                      onChange={(e) => setConfig({ ...config, scene_description: e.target.value })}
                      className="w-full h-32 bg-black/50 border border-cyan-500/30 rounded-lg p-3 
                               text-white/90 font-mono text-sm resize-none
                               focus:outline-none focus:border-cyan-500/50 focus:bg-black/70
                               placeholder-white/30 transition-all"
                      placeholder="Describe your video... Industrial warehouse, neon-lit streets, concrete bunker..."
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-cyan-400 mb-3 uppercase tracking-wider">
                      Visual Style Protocol
                    </label>
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(VISUAL_STYLES).map(([key, style]) => (
                        <button
                          key={key}
                          onClick={() => setConfig({ ...config, visual_style: key })}
                          className={`
                            relative p-4 rounded-lg border transition-all duration-300
                            ${config.visual_style === key 
                              ? 'bg-black/70 border-cyan-500/50' 
                              : 'bg-black/30 border-white/10 hover:border-white/20'}
                          `}
                        >
                          {config.visual_style === key && (
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-cyan-500/10 to-transparent" />
                          )}
                          <div className="relative">
                            <div className="text-sm font-mono text-white/90 mb-1">{style.label}</div>
                            <div className="text-xs text-white/50">{style.description}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 py-3 bg-black/50 border border-white/10 rounded-lg 
                               text-white/50 font-mono hover:border-white/20 transition-all"
                    >
                      BACK
                    </button>
                    <button
                      onClick={() => setCurrentStep(3)}
                      className="flex-1 py-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 
                               border border-cyan-500/30 rounded-lg text-cyan-400 font-mono
                               hover:from-cyan-500/30 hover:to-blue-500/30 transition-all
                               flex items-center justify-center gap-2"
                    >
                      CONTINUE
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Parameters */}
              {currentStep === 3 && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">
                        Duration (Seconds)
                      </label>
                      <input
                        type="number"
                        value={config.video_duration}
                        onChange={(e) => setConfig({ ...config, video_duration: parseInt(e.target.value) })}
                        className="w-full bg-black/50 border border-cyan-500/30 rounded-lg p-3
                                 text-white/90 font-mono text-sm
                                 focus:outline-none focus:border-cyan-500/50 focus:bg-black/70
                                 transition-all"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">
                        Budget Limit ($)
                      </label>
                      <input
                        type="number"
                        value={config.budget_limit}
                        onChange={(e) => setConfig({ ...config, budget_limit: parseInt(e.target.value) })}
                        className="w-full bg-black/50 border border-cyan-500/30 rounded-lg p-3
                                 text-white/90 font-mono text-sm
                                 focus:outline-none focus:border-cyan-500/50 focus:bg-black/70
                                 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">
                      Quality Threshold
                    </label>
                    <div className="relative">
                      <input
                        type="range"
                        min="50"
                        max="100"
                        value={config.quality_threshold}
                        onChange={(e) => setConfig({ ...config, quality_threshold: parseInt(e.target.value) })}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-white/50 mt-2">
                        <span>DRAFT</span>
                        <span className="text-cyan-400 font-mono">{config.quality_threshold}%</span>
                        <span>PRODUCTION</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">
                      Max Iterations Per Stage
                    </label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 5, 10].map(num => (
                        <button
                          key={num}
                          onClick={() => setConfig({ ...config, max_iterations: num })}
                          className={`
                            flex-1 py-2 rounded-lg border font-mono transition-all
                            ${config.max_iterations === num
                              ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                              : 'bg-black/30 border-white/10 text-white/50 hover:border-white/20'}
                          `}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => setCurrentStep(2)}
                      className="flex-1 py-3 bg-black/50 border border-white/10 rounded-lg 
                               text-white/50 font-mono hover:border-white/20 transition-all"
                    >
                      BACK
                    </button>
                    <button
                      onClick={() => setCurrentStep(4)}
                      className="flex-1 py-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 
                               border border-cyan-500/30 rounded-lg text-cyan-400 font-mono
                               hover:from-cyan-500/30 hover:to-blue-500/30 transition-all
                               flex items-center justify-center gap-2"
                    >
                      CONTINUE
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Advanced Settings */}
              {currentStep === 4 && (
                <div className="space-y-6 animate-fadeIn">
                  <div>
                    <label className="block text-xs font-mono text-cyan-400 mb-3 uppercase tracking-wider">
                      Advanced Configuration
                    </label>
                    
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-white/10 hover:border-white/20 cursor-pointer transition-all">
                        <input
                          type="checkbox"
                          checked={config.enable_checkpointing}
                          onChange={(e) => setConfig({ ...config, enable_checkpointing: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <div>
                          <div className="text-sm text-white/90">Enable Checkpointing</div>
                          <div className="text-xs text-white/50">Save progress after each stage</div>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">
                      Custom Instructions (Optional)
                    </label>
                    <textarea
                      value={config.custom_instructions}
                      onChange={(e) => setConfig({ ...config, custom_instructions: e.target.value })}
                      className="w-full h-24 bg-black/50 border border-cyan-500/30 rounded-lg p-3 
                               text-white/90 font-mono text-sm resize-none
                               focus:outline-none focus:border-cyan-500/50 focus:bg-black/70
                               placeholder-white/30 transition-all"
                      placeholder="Additional directives for the pipeline..."
                    />
                  </div>

                  {/* Summary */}
                  <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                    <div className="text-xs font-mono text-cyan-400 mb-2 uppercase">Execution Summary</div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-white/70">
                      <div>Curriculum: {config.selected_lesson_path.split('/').slice(-2).join('/')}</div>
                      <div>Duration: {config.video_duration}s</div>
                      <div>Budget: ${config.budget_limit}</div>
                      <div>Quality: {config.quality_threshold}%</div>
                      <div>Profile: {config.profile_id ? (profiles.find(p => p.id === config.profile_id)?.name || 'Selected') : '<None>'}</div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={() => setCurrentStep(3)}
                      className="flex-1 py-3 bg-black/50 border border-white/10 rounded-lg 
                               text-white/50 font-mono hover:border-white/20 transition-all"
                    >
                      BACK
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={isInitiating}
                      className={`
                        flex-1 py-3 rounded-lg font-mono transition-all
                        flex items-center justify-center gap-2
                        ${isInitiating
                          ? 'bg-gray-500/20 border border-gray-500/30 text-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-cyan-500/20 to-green-500/20 border border-cyan-500/30 text-cyan-400 hover:from-cyan-500/30 hover:to-green-500/30'}
                      `}
                    >
                      {isInitiating ? (
                        <>
                          <Cpu className="w-4 h-4 animate-spin" />
                          INITIALIZING...
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4" />
                          INITIATE PIPELINE
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Status indicator */}
            {isInitiating && (
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 relative">
                    <div className="absolute inset-0 bg-cyan-500/20 rounded-full animate-ping" />
                    <div className="absolute inset-0 bg-cyan-500/30 rounded-full animate-pulse" />
                    <div className="relative w-full h-full flex items-center justify-center">
                      <Zap className="w-8 h-8 text-cyan-400" />
                    </div>
                  </div>
                  <div className="text-white/90 font-mono">INITIALIZING PIPELINE</div>
                  <div className="text-xs text-white/50 mt-1">Configuring execution parameters...</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FuturisticExecutionModal;